import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { apiCreateSupplier, type CreateSupplierInput } from '@/lib/api'

const createSchema = z.object({
  name: z.string().trim().min(1, 'Le nom est requis').max(255),
  email: z.string().trim().email('Email invalide').max(320),
  company: z.string().trim().max(255).optional(),
  phone: z.string().trim().max(50).optional(),
  notes: z.string().max(2000).optional(),
})

const EMPTY: CreateSupplierInput = {
  name: '',
  email: '',
  company: '',
  phone: '',
  notes: '',
}

export function CreateSupplierDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (next: boolean) => void
  onCreated: () => void
}) {
  const [form, setForm] = useState<CreateSupplierInput>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  function handleClose(next: boolean) {
    // P21 : empêcher la fermeture pendant la soumission pour éviter la perte
    // de données saisies et les mutations d'état après démontage.
    if (!next && submitting) return
    if (!next) {
      setForm(EMPTY)
      setErrors({})
    }
    onOpenChange(next)
  }

  function updateField<K extends keyof CreateSupplierInput>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // P15 : un Enter pressé pendant qu'une soumission est en cours ne doit
    // pas déclencher de création doublon (protection ceinture+bretelles en plus
    // de `disabled` sur le bouton Créer).
    if (submitting) return
    const parsed = createSchema.safeParse(form)
    if (!parsed.success) {
      const next: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const path = issue.path[0]
        if (typeof path === 'string') next[path] = issue.message
      }
      setErrors(next)
      return
    }
    setErrors({})
    setSubmitting(true)
    try {
      await apiCreateSupplier({
        name: parsed.data.name,
        email: parsed.data.email,
        company: parsed.data.company || null,
        phone: parsed.data.phone || null,
        notes: parsed.data.notes || null,
      })
      toast.success('Fournisseur créé')
      onCreated()
      handleClose(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ajouter un fournisseur</DialogTitle>
          <DialogDescription>
            Saisissez les informations du fournisseur. Vous pourrez ensuite l'ajouter à une campagne.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="supplier-name">Nom *</Label>
            <Input
              id="supplier-name"
              className="h-12"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? 'supplier-name-error' : undefined}
              required
            />
            {errors.name && (
              <p id="supplier-name-error" className="text-sm text-destructive">
                {errors.name}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="supplier-email">Email *</Label>
            <Input
              id="supplier-email"
              type="email"
              className="h-12"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'supplier-email-error' : undefined}
              required
            />
            {errors.email && (
              <p id="supplier-email-error" className="text-sm text-destructive">
                {errors.email}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="supplier-company">Entreprise</Label>
            <Input
              id="supplier-company"
              className="h-12"
              value={form.company ?? ''}
              onChange={(e) => updateField('company', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="supplier-phone">Téléphone</Label>
            <Input
              id="supplier-phone"
              className="h-12"
              value={form.phone ?? ''}
              onChange={(e) => updateField('phone', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="supplier-notes">Notes</Label>
            <Textarea
              id="supplier-notes"
              rows={3}
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Création…' : 'Créer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
